// The UI thread's handle on the data worker: typed calls, one change stream. Views and view
// models import `data` and never `fetch` or SQL — docs/PERFORMANCE.md's layer split, kept honest
// by there being no other way in.
import type { StoreTable } from '../data/db/database';
import type { EngineSignal } from '../data/sync/syncEngine';
import type { Method, RequestMessage, ResponseMessage, WorkerApi, WorkerEvent } from './protocol';

type ChangeListener = (tables: Set<StoreTable>) => void;
type SignalListener = (signal: EngineSignal) => void;

class DataClient {
  private worker: Worker | null = null;
  private nextId = 1;
  private pending = new Map<number, { resolve: (v: unknown) => void; reject: (e: Error) => void }>();
  private changeListeners = new Set<ChangeListener>();
  private signalListeners = new Set<SignalListener>();
  /** The newest connection state, for a screen that subscribes after the socket already moved. */
  lastConnection: Extract<EngineSignal, { kind: 'connection' }> | null = null;

  start(): void {
    if (this.worker) return;
    this.worker = new Worker(new URL('../data/worker.ts', import.meta.url), { type: 'module', name: 'satori-data' });
    this.worker.onmessage = (event: MessageEvent<ResponseMessage | WorkerEvent>) => {
      const msg = event.data;
      if ('event' in msg) {
        if (msg.event === 'changes') {
          const set = new Set(msg.tables);
          for (const l of this.changeListeners) l(set);
        } else {
          if (msg.signal.kind === 'connection') this.lastConnection = msg.signal;
          for (const l of this.signalListeners) l(msg.signal);
        }
        return;
      }
      const waiter = this.pending.get(msg.id);
      if (!waiter) return;
      this.pending.delete(msg.id);
      if (msg.ok) waiter.resolve(msg.value);
      else waiter.reject(new Error(msg.error));
    };
    this.worker.onerror = (e) => console.error('[satori] data worker error', e.message);
  }

  /** Releases the database for another tab ("Usar aquí" there). */
  terminate(): void {
    this.worker?.terminate();
    this.worker = null;
    for (const w of this.pending.values()) w.reject(new Error('worker stopped'));
    this.pending.clear();
  }

  call<M extends Method>(method: M, ...args: Parameters<WorkerApi[M]>): Promise<ReturnType<WorkerApi[M]>> {
    if (!this.worker) this.start();
    const id = this.nextId++;
    const message: RequestMessage = { id, method, args };
    return new Promise((resolve, reject) => {
      this.pending.set(id, { resolve: resolve as (v: unknown) => void, reject });
      this.worker!.postMessage(message);
    });
  }

  onChanges(listener: ChangeListener): () => void {
    this.changeListeners.add(listener);
    return () => this.changeListeners.delete(listener);
  }

  onSignal(listener: SignalListener): () => void {
    this.signalListeners.add(listener);
    return () => this.signalListeners.delete(listener);
  }
}

export const data = new DataClient();

// MARK: - One tab owns the database

const LOCK_NAME = 'satori-chat-db';
const CHANNEL_NAME = 'satori-chat-tabs';

/**
 * Takes the database lock if it is free. Resolves true once held; false when another tab has it.
 * The lock is held until `releaseTab()` or the tab closes — the browser frees it on its own then,
 * which is what makes a crashed tab unable to lock the chat away for ever.
 */
let releaseLock: (() => void) | null = null;
let tabChannel: BroadcastChannel | null = null;

let acquiring: Promise<boolean> | null = null;

export function acquireTab(onTakenElsewhere: () => void): Promise<boolean> {
  acquiring ??= acquireTabOnce(onTakenElsewhere);
  return acquiring;
}

async function acquireTabOnce(onTakenElsewhere: () => void): Promise<boolean> {
  if (!('locks' in navigator)) return true; // very old browser: no arbitration available
  tabChannel ??= new BroadcastChannel(CHANNEL_NAME);
  const got = await new Promise<boolean>((resolve) => {
    void navigator.locks.request(LOCK_NAME, { ifAvailable: true }, (lock) => {
      if (!lock) {
        resolve(false);
        return undefined;
      }
      resolve(true);
      return new Promise<void>((release) => {
        releaseLock = release;
      });
    });
  });
  if (got) {
    tabChannel.onmessage = (e) => {
      if (e.data === 'take-over') {
        // Another tab asked for the chat: let go of everything, then the lock.
        data.terminate();
        releaseLock?.();
        releaseLock = null;
        onTakenElsewhere();
      }
    };
  }
  return got;
}

/** "Usar aquí": ask the owning tab to let go, then wait for the lock. */
export async function takeOverTab(onTakenElsewhere: () => void): Promise<void> {
  tabChannel ??= new BroadcastChannel(CHANNEL_NAME);
  tabChannel.postMessage('take-over');
  await new Promise<void>((resolve) => {
    void navigator.locks.request(LOCK_NAME, (lock) => {
      resolve();
      return new Promise<void>((release) => {
        releaseLock = release;
        void lock;
      });
    });
  });
  tabChannel.onmessage = (e) => {
    if (e.data === 'take-over') {
      data.terminate();
      releaseLock?.();
      releaseLock = null;
      onTakenElsewhere();
    }
  };
}
